import './AuthorDetail.css';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { fetchAuthorById } from '../../services/authorService';
import { fetchSimplifiedBooksByAuthor } from '../../services/bookService';
import { formatAuthorLifespan } from '../../utils/dateUtils';
import { BOOK_TYPES } from '../../constants/bookConstants';

function AuthorDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [author, setAuthor] = useState(null);
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadAuthor = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [authorData, booksData] = await Promise.all([
                fetchAuthorById(id),
                fetchSimplifiedBooksByAuthor(id)
            ]);

            setAuthor(authorData);
            setBooks(booksData || []);
        } catch (err) {
            setError('Nepodařilo se načíst autora. Zkuste to prosím znovu.');
            console.error('Error loading author:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        loadAuthor();
    }, [loadAuthor]);

    const getBookTypeLabel = (typeValue) => {
        const bookType = BOOK_TYPES.find(type => type.value === typeValue);
        return bookType ? bookType.label : typeValue;
    };

    if (loading) {
        return (
            <div className="author-detail-page">
                <div className="author-detail-container">
                    <div className="loading-container">
                        <p>Načítání autora...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !author) {
        return (
            <div className="author-detail-page">
                <div className="author-detail-container">
                    <div className="not-found">
                        <h1>{error || 'Autor nenalezen'}</h1>
                        <button onClick={() => navigate('/autori')} className="back-button">
                            Zpět na seznam autorů
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="author-detail-page">
            <div className="author-detail-container">
                <button onClick={() => navigate('/autori')} className="back-button">
                    ← Zpět na autory
                </button>

                {/* Header Section */}
                <header className="author-header">
                    <h1 className="author-detail-name">{author.name}</h1>
                    <p className="author-dates">
                        {formatAuthorLifespan(author.birthDate, author.deathDate, true)}
                    </p>
                </header>

                {/* Biography Section */}
                <section className="detail-section">
                    <h2 className="section-title">Životopis</h2>
                    <div className="section-content biography">
                        <ReactMarkdown>{author.info}</ReactMarkdown>
                    </div>
                </section>

                {/* Books Grid Section */}
                {books.length > 0 && (
                    <section className="detail-section">
                        <h2 className="section-title">Díla v databázi</h2>
                        <div className="author-books-grid">
                            {books.map((book) => (
                                <div
                                    key={book.id}
                                    className="author-book-tile"
                                    onClick={() => navigate(`/knihy/${book.id}`)}
                                >
                                    <h3 className="author-book-tile-title">{book.bookTitle}</h3>
                                    <p className="author-book-tile-type">{getBookTypeLabel(book.bookType)}</p>
                                    {book.shortSummary && (
                                        <p className="author-book-tile-description">{book.shortSummary}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Books Section */}
                {author.references && author.references.length > 0 && (
                    <section className="detail-section books-section">
                        <h2 className="section-title">Knihy v naší databázi</h2>
                        <div className="books-list">
                            {author.references.map((book) => (
                                <Link
                                    key={book.bookId}
                                    to={`/knihy/${book.bookId}`}
                                    className="book-link"
                                >
                                    <div className="book-item">
                                        <span className="book-icon">📖</span>
                                        <span className="book-name">{book.bookName}</span>
                                        <span className="arrow">→</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

export default AuthorDetail;
